import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useStore } from 'react-redux';
import AsyncSelect from 'react-select/async';
import { RootState } from 'tsx/types/state';

import { Row } from '~libs/reduxUtils';

import ClickableIcon from '~components/ClickableIcon';
import { faRefresh } from '~components/icon';

import { InputProps } from '../props';
import { SelectionSummary, summaryAriaLabel, summaryComponents } from './selectionSummary';
import { wideMenuProps } from './wideMenu';

interface Option {
  value: string | number | undefined;
  label: string | undefined;
}

interface ComponentProps extends InputProps {
  /** Opt-in: several values. `value` is then an array and `onChange` receives an array. */
  isMulti?: boolean;
  /** Opt-in: a menu wider than the control, portalled to the body, one line per option. */
  wideMenu?: boolean;
  /**
   * Opt-in, with `isMulti`, for a narrow control: show one line of text (the label, or "3 clients")
   * instead of a chip per value. The chosen options stay ticked in the open menu.
   */
  summariseSelection?: SelectionSummary;
}

const SelectLazy: React.FC<ComponentProps> = ({
  id,
  value,
  disabled,
  placeholder,
  selectorOptions,
  selectorActions,
  selectorParams,
  onChange,
  clearable = true,
  manualReload = false,
  initialValue,
  initialOption,
  isMulti = false,
  wideMenu = false,
  summariseSelection,
}) => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  // Bumped on a forced manual reload so the AsyncSelect below remounts and drops
  // its internal react-select cacheOptions cache, which otherwise keeps serving a
  // stale result for a search string repeated after the reload.
  const [reloadNonce, setReloadNonce] = useState(0);
  const fetchedSelectorOptions = useAppSelector(selectorOptions || (() => []));

  // Tracks the in-flight fetch so a keystroke, a focus and the reload button all
  // share one request instead of firing three, and so loadOptions has something
  // to await.
  const pendingLoad = useRef<Promise<void> | null>(null);
  const loadAttempted = useRef(false);

  const buildOptions = (rows: Row[]): Option[] =>
    (!initialOption ? rows : [...rows, initialOption]).map(({ id, name }) => ({ value: id, label: name }) as Option);

  const options = buildOptions(fetchedSelectorOptions);

  // Labels of every option seen so far, so a chosen value keeps its label after a search narrows the
  // list to something that no longer contains it.
  const seenLabels = useRef(new Map<string | number | undefined, string | undefined>());
  options.forEach((option) => seenLabels.current.set(option.value, option.label));

  const [valueToDisplay, setValueToDisplay] = useState<Option | { label: string; value: string | null } | null>(null);

  const filterOptions = (available: Option[], inputValue: string) =>
    available.filter((option) => option.label?.toLowerCase().includes(inputValue.toLowerCase()));

  const refreshOptions = (force = false): Promise<void> => {
    if (!selectorActions) return Promise.resolve();
    if (pendingLoad.current) return pendingLoad.current;
    if (!force && loadAttempted.current) return Promise.resolve();

    loadAttempted.current = true;
    setIsRefreshing(true);

    const request = Promise.resolve(dispatch(selectorActions(selectorParams)))
      .then(() => {
        setOptionsLoaded(true);
      })
      .catch((error: unknown) => {
        // Clear the attempt flag so a later focus or keystroke can retry rather
        // than leaving the control permanently empty after one failed fetch.
        loadAttempted.current = false;
        console.error(`Error loading options for ${id}: ${error}`);
      })
      .finally(() => {
        pendingLoad.current = null;
        setIsRefreshing(false);
      });

    pendingLoad.current = request;
    return request;
  };

  const loadOptions = async (inputValue: string) => {
    // The option list is fetched lazily, so a keystroke can land before the data
    // has arrived. Awaiting the fetch keeps react-select in its loading state and
    // filters against the settled list, instead of reporting "No options" for a
    // record that does exist and never re-filtering once the data lands.
    await refreshOptions();

    // Read straight from the store: the awaited fetch has updated it, but this
    // closure still holds the pre-fetch render's options.
    const available = selectorOptions ? buildOptions(selectorOptions(store.getState())) : options;
    available.forEach((option) => seenLabels.current.set(option.value, option.label));
    return filterOptions(available, inputValue);
  };

  useEffect(() => {
    if (initialOption === undefined && initialValue === undefined) {
      const selectedOption = options.find((option) => option.value === value);
      setValueToDisplay(selectedOption ?? null);
    } else if (optionsLoaded) {
      const selectedOption = options.find((option) => option.value === value);
      setValueToDisplay(selectedOption ?? null);
    } else if (initialValue && value === initialValue.value) {
      setValueToDisplay(initialValue);
    } else if (value !== null && value !== undefined) {
      const selectedOption = options.find((option) => option.value === value);
      setValueToDisplay(selectedOption ?? null);
    } else {
      setValueToDisplay(null);
    }
  }, [fetchedSelectorOptions, optionsLoaded, value, initialValue, initialOption]);

  const multiValue: Option[] = isMulti
    ? (Array.isArray(value) ? value : []).map((v: string | number) => ({
        value: v,
        label: seenLabels.current.get(v) ?? String(v),
      }))
    : [];

  const handleFocus = () => {
    refreshOptions();
  };

  const handleManualReload = () => {
    setReloadNonce((nonce) => nonce + 1);
    refreshOptions(true);
  };

  return (
    <div className="align-items-center row" data-cy={`${id}-selectlazy`}>
      <div className="col">
        <AsyncSelect
          // Remounts (and drops react-select's internal cacheOptions cache) whenever
          // a forced manual reload bumps reloadNonce - see handleManualReload.
          key={reloadNonce}
          // inputId (not id) puts the field id on the combobox input itself, so the
          // form-field <Label for={id}> labels the input rather than the container.
          inputId={id}
          name={id}
          classNamePrefix={'form-input-select'}
          isDisabled={disabled}
          placeholder={placeholder ?? '-'}
          isClearable={clearable}
          isLoading={isRefreshing}
          value={isMulti ? multiValue : valueToDisplay}
          isMulti={isMulti}
          closeMenuOnSelect={!isMulti}
          {...(wideMenu ? wideMenuProps : {})}
          {...(summariseSelection && isMulti
            ? {
                hideSelectedOptions: false,
                summary: summariseSelection,
                'aria-label': summaryAriaLabel(summariseSelection.ariaLabel ?? placeholder, multiValue.length),
                components: { ...(wideMenu ? wideMenuProps.components : {}), ...summaryComponents },
              }
            : {})}
          loadOptions={loadOptions}
          onFocus={handleFocus}
          onChange={(selected, event) => {
            if (isMulti) {
              onChange?.(
                event.name ?? '',
                ((selected as Option[] | null) ?? []).map((option) => option.value),
              );
              return;
            }
            const single = selected as Option | null;
            onChange && onChange(event.name ?? '', single ? single.value : null);
          }}
          defaultOptions={options}
          cacheOptions={optionsLoaded}
        />
      </div>
      {manualReload && (
        <div className="col-sm-1 ms-2">
          <ClickableIcon
            id={id}
            icon={faRefresh}
            size="small"
            color="success"
            onClick={handleManualReload}
            disabled={isRefreshing}
            toolTip="Reload"
            dataCy={`${id}-reload`}
          />
        </div>
      )}
    </div>
  );
};

export default SelectLazy;
